import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import { Wind, BookOpen } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// 2026 formaldehyde-free binders for glasswool (Knauf ECOSE / Owens Corning EcoTouch / Johns Manville Formaldehyde-Free)
const BINDERS = {
  bio:     { name: "Bio-resin (ECOSE/PURE)", short: "BIO", color: "#39FF14",
             flame_spread: 15, smoke_dev: 35, loi_delta: +1.5, voc: "Negligible",
             descKey: "gw.binder.bio.desc",
             descEn: "Plant-based sugar derivative · formaldehyde-free · Cradle-to-Cradle Silver" },
  acrylic: { name: "Acrylic (BondTec)",      short: "ACR", color: "#00E5FF",
             flame_spread: 22, smoke_dev: 75, loi_delta: 0,   voc: "Low",
             descKey: "gw.binder.acrylic.desc",
             descEn: "Thermoset acrylic · low emission · standard performance" },
  phenolic:{ name: "Phenolic (traditional)", short: "PHE", color: "#FF8C42",
             flame_spread: 25, smoke_dev: 50, loi_delta: -0.5, voc: "Trace formaldehyde",
             descKey: "gw.binder.phenolic.desc",
             descEn: "Standard phenol-formaldehyde · EU restricted post-2027" },
};

const GW_BASE = {
  lambda: 0.035,           // W/mK
  loi: 75,                  // %
  flame_spread_base: 22,    // ASTM E84
  smoke_dev_base: 65,
};

export default function GlasswoolModule() {
  const { t } = useI18n();
  const [binder, setBinder] = useState("bio");
  const [content, setContent] = useState(8);   // % binder content

  const data = BINDERS[binder];

  const pred = useMemo(() => {
    // Flame spread and smoke increase mildly with binder content but plateau
    const fs = data.flame_spread + content * 0.4;
    const sm = data.smoke_dev + content * 1.2;
    const loi = GW_BASE.loi + data.loi_delta + (content - 8) * 0.3;
    return {
      flame_spread: Math.round(fs * 10) / 10,
      smoke_dev: Math.round(sm),
      loi: Math.round(loi * 10) / 10,
      asTM_class: fs <= 25 && sm <= 450 ? "Class A" : fs <= 75 ? "Class B" : "Class C",
    };
  }, [binder, content, data]);

  // Comparison bar data
  const compareData = useMemo(() => Object.entries(BINDERS).map(([k, b]) => ({
    name: b.short,
    "Flame Spread": b.flame_spread,
    "Smoke Index": b.smoke_dev,
    color: b.color,
    active: k === binder,
  })), [binder]);

  return (
    <section className="border-2 border-[#FFCC00] bg-[#1F1A05]" data-testid="glasswool-module">
      <header className="border-b border-[#FFCC00] px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#FFD700' }}>
        <div className="flex items-center gap-2">
          <Wind className="w-4 h-4" style={{ color: '#000000' }}/>
          <span className="gw-title-gold font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#000000', fontWeight: 700, opacity: 1, letterSpacing: '0.02em' }}>
            {t("gw.title", "Glass Wool Module · Formaldehyde-Free Binder Analysis")}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: '#000000', fontWeight: 700, opacity: 0.85 }}>ASTM E84 · UL 723</span>
      </header>
      <div className="p-4 space-y-4">
        <p className="gw-desc font-mono text-[11px] leading-relaxed">
          {t("gw.desc.prefix", "During 2024-2026 the EU and the US restricted formaldehyde emissions. Modern glass-wool manufacturers are switching to bio-resin (plant-based) binders (")}
          <strong className="gw-brand">Knauf ECOSE</strong>
          {t("gw.desc.sep1", ", ")}
          <strong className="gw-brand">Owens Corning Pure</strong>
          {t("gw.desc.sep2", ", ")}
          <strong className="gw-brand">Johns Manville F-Free</strong>
          {t("gw.desc.suffix", "). ASTM E84 testing measures the Flame Spread Index (FSI) and Smoke Developed Index (SDI).")}
        </p>

        {/* Binder selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="binder-selector">
          {Object.entries(BINDERS).map(([key, b]) => {
            const isActive = binder === key;
            return (
              <button key={key} onClick={() => setBinder(key)}
                className="p-3 text-left transition-all"
                style={{
                  backgroundColor: isActive ? '#FFD700' : '#FEF9C3',
                  color: '#000000',
                  border: isActive ? `2px solid ${b.color}` : '1px solid #FDE047',
                  borderRadius: '6px',
                  boxShadow: isActive ? '0 0 8px rgba(255, 215, 0, 0.4)' : 'none',
                  fontWeight: 600,
                  opacity: 1
                }}
                data-testid={`binder-${key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm" style={{ color: '#000000', fontWeight: 700, opacity: 1 }}>
                    {b.short}
                  </span>
                  <span className="font-mono text-[10px]" style={{ color: '#1A202C', fontWeight: 600, opacity: 0.85 }}>VOC: {b.voc}</span>
                </div>
                <div className="font-mono text-[11px] mt-1" style={{ color: '#000000', fontWeight: 600, opacity: 1 }}>{b.name}</div>
                <div className="font-mono text-[10px] mt-1 leading-snug" style={{ color: '#1A202C', fontWeight: 500, opacity: 0.85 }}>{t(b.descKey, b.descEn)}</div>
              </button>
            );
          })}
        </div>

        {/* Content slider */}
        <div data-testid="binder-content-slider">
          <div className="flex items-center justify-between mb-1" style={{ backgroundColor: '#FF8C00', padding: '6px 14px', borderRadius: '8px' }}>
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>
              {t("gw.slider", "Binder Content (%)")}
            </span>
            <span className="font-mono text-lg" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 800, opacity: 1 }}>
              {content.toFixed(1)}%
            </span>
          </div>
          <Slider value={[content]} min={4} max={14} step={0.5}
            onValueChange={(v) => setContent(v[0])} className="my-2"/>
          <div className="font-mono text-[10px]" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 600, opacity: 1 }}>4% (low-binder) → 14% (high-tack)</div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F1F1F] border border-[#1F1F1F]" data-testid="gw-results">
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>FSI · ASTM E84</div>
            <div className="font-mono text-xl" style={{ color: '#E1AD01', fontWeight: 800, opacity: 1, WebkitTextFillColor: '#E1AD01' }}>
              {pred.flame_spread}
            </div>
            <div className="font-mono text-[10px]" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 600, opacity: 1 }}>Flame Spread Index</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>SDI · ASTM E84</div>
            <div className="font-mono text-xl" style={{ color: '#E1AD01', fontWeight: 800, opacity: 1, WebkitTextFillColor: '#E1AD01' }}>
              {pred.smoke_dev}
            </div>
            <div className="font-mono text-[10px]" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 600, opacity: 1 }}>Smoke Developed Index</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>LOI</div>
            <div className="font-mono text-xl" style={{ color: '#E1AD01', fontWeight: 800, opacity: 1, WebkitTextFillColor: '#E1AD01' }}>{pred.loi}</div>
            <div className="font-mono text-[10px]" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 600, opacity: 1 }}>% O₂ · ISO 4589-2</div>
          </div>
          <div className="bg-[#050505] p-3">
            <div className="tech-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>{t("gw.cell.class", "CLASS")}</div>
            <div className="font-mono text-xl" style={{ color: '#E1AD01', fontWeight: 800, opacity: 1, WebkitTextFillColor: '#E1AD01' }}>
              {pred.asTM_class}
            </div>
            <div className="font-mono text-[10px]" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 600, opacity: 1 }}>UL 723 / NFPA 255</div>
          </div>
        </div>

        {/* Comparison chart */}
        <div className="rd-chart-container border border-[#1F1F1F] bg-[#050505] p-3" data-chart="true" data-testid="binder-compare-chart" style={{ backgroundColor: '#000000' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs uppercase tracking-wider text-[#FFCC00]">
              {t("gw.chart.compare", "Binder Comparison · FSI & SDI")}
            </span>
            <span className="font-mono text-[10px] text-[#8B8B95]">ASTM E84 nominal</span>
          </div>
          <div style={{ height: 200, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={compareData} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
                <CartesianGrid stroke="#1F1F1F"/>
                <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "#FFFFFF" }}/>
                <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}/>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #FFCC00",
                  fontFamily: "IBM Plex Mono", fontSize: 11, color: "#FFF" }}/>
                <Bar dataKey="Flame Spread" fill="#FF8C42" isAnimationActive={false}/>
                <Bar dataKey="Smoke Index" fill="#FFCC00" isAnimationActive={false}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <article className="border border-[#FFCC00] bg-[#1F1A05] p-3" data-testid="prof-note-glasswool">
          <header className="flex items-center gap-2 mb-2">
            <BookOpen className="w-3.5 h-3.5 text-[#FFCC00]"/>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFCC00]">
              {t("gw.note.title", "Professor's Note · Bio-Resin Transition")}
            </span>
          </header>
          <p className="text-sm leading-relaxed text-white">
            {t("gw.note.body", "Bio-resin (plant-based sugar derivative) binders reduce formaldehyde emissions to zero while preserving fire performance — ASTM E84 FSI values stay within the Class A (≤25) band. Driven by certification requirements for LEED v4.1 and the WELL Building Standard, the post-2027 market share is expected to exceed 60%.")}
          </p>
        </article>
      </div>
    </section>
  );
}
